import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  inject,
  AfterContentInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RoundPipe } from '../../../pipes/round.pipe';
import { Observable } from 'rxjs';
import { SideNavItem } from '../rankings/models/sideNavItem';
import { SideNavItemsMap } from '../rankings/models/sideNavItemsMap';
import { KYD_REPORT_CONFIG, OVERALL_RISK_OPTIONS } from './KYD_REPORT_CONFIG';
import { configure } from 'instantsearch.js/es/widgets';
import { connectHits } from 'instantsearch.js/es/connectors';
import { HitsRenderState } from 'instantsearch.js/es/connectors/hits/connectHits';
import { InstantSearchService } from 'app/services/algolia/algolia-instant-search.service';
import { MainFilterItem } from '../rankings/models/mainFilterItem';
import { DropdownItem } from '../rankings/models/dropdownItem';
import { DeveloperService } from 'app/services/developer.service';
import { Widget } from 'instantsearch.js';
import connectSearchBox, {
  SearchBoxRenderState
} from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import { DeveloperRanking } from '../rankings/models/developerRanking';
import { RankingsPageBaseComponent } from '../rankings-page-base.component';
import { ReportConfig } from '../rankings/REPORT_CONFIG';

declare let jQuery: any;

@Component({
  selector: 'app-know-your-developer',
  templateUrl: './know-your-developer.component.html',
  styleUrls: ['./know-your-developer.component.scss']
})
export class KnowYourDeveloperComponent
  extends RankingsPageBaseComponent
  implements OnInit, OnDestroy, AfterContentInit
{
  currentConfig: ReportConfig;
  gridData: Array<DeveloperRanking>;
  sideNavItems: Array<SideNavItem>;
  sideNavItemsMaps: Array<SideNavItemsMap>;
  currentMainFilterItems: Array<MainFilterItem>;

  currentSideNavItem: SideNavItem;

  showInsight = false;
  selectedDeveloperName: string;
  selectedDate: string;
  navigateTo: string;
  selectedGroup = 'app';
  searchParamModel: string;

  @ViewChild('sideNav') sideNav: ElementRef;

  // -- Algolia Integration
  showAlgoliaResults = false;
  formattedFilterQuery = '';
  restrictSearchableAttributes: string[] = ['title', 'publisherName'];
  attributesToRetrieve: string[] = [
    'title',
    'kydDeveloperName',
    'appIcon',
    'kydOverallRisk'
  ]; // '*' means all attributes

  // ── State exposed to the template ────────────────────────────────────────
  // Both are public so the HTML template can bind to them directly.
  searchBoxState?: SearchBoxRenderState;
  hitsState?: HitsRenderState;

  // ── Private ───────────────────────────────────────────────────────────────
  private iss = inject(InstantSearchService);
  private cdr = inject(ChangeDetectorRef);

  // Keep references for cleanup in ngOnDestroy
  private searchBoxWidget!: Widget;
  private hitsWidget!: Widget;
  private configureWidget!: Widget;
  private debounceTimer: any = null;

  constructor(
    route: ActivatedRoute,
    private _developerService: DeveloperService
  ) {
    super(route);
    this.loading = false;
    this.loadingData = false;
    this.showLoadingMore = false;
    this.showLoadMore = false;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this._developerService.route = this._route;
    this.initComponent();

    this._route.queryParamMap.subscribe(params => {
      const name = params.get('name');
      if (name && name != this.selectedDeveloperName) {
        this.selectedDeveloperName = name;
      }
    });

    this._pageMetaService.setStructuredData(this.getStructuredData());

    // ── connectSearchBox ───────────────────────────────────────────────────
    // The render function is called on every search state update.
    // Assign the state, then manually trigger Angular change detection
    // since the connector runs outside Angular's zone.
    this.searchBoxWidget = connectSearchBox(state => {
      this.searchBoxState = state;
      this.showAlgoliaResults = !this.showInsight && state.query?.length > 0;
      if (state.query.length == 0) {
        this.clearSearchGrid();
      }
      this.cdr.detectChanges();
    })({});

    // ── connectHits ────────────────────────────────────────────────────────
    this.hitsWidget = connectHits(state => {
      this.hitsState = state;
      this.cdr.detectChanges();
    })({});

    this.configureWidget = this.buildConfigureWidget();

    this.iss.addWidgets([
      this.searchBoxWidget,
      this.hitsWidget,
      this.configureWidget
    ]);
  }

  getStructuredData() {
    return {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: "Pixalate's Know Your Developer (KYD)",
      description:
        "Pixalate's Know Your Developer (KYD) is a free, public, and continuously updated database to help parents, schools, and child safety advocates identify privacy and safety risks among mobile app developers. KYD evaluates 356,095 ad-funded mobile app developers operating more than 3.1M apps across the Apple App Store and Google Play Store, assessing and rating online child safety, privacy compliance, and advertising fraud risk for each developer's app portfolio.",
      url: 'pixalate.com/kyd-data/',
      datePublished: '2026-02-01',
      dateModified: '2026-02-01',
      publisher: {
        '@type': 'Organization',
        name: 'Pixalate'
      },
      distribution: {
        '@type': 'DataDownload',
        encodingFormat: 'CSV',
        contentUrl: 'pixalate.com/data/know-your-developer-ratings-202512.csv'
      }
    };
  }

  initComponent() {
    if (!this.loading && !this.loadingData) {
      const foundConfig = KYD_REPORT_CONFIG[0];
      this.currentConfig = foundConfig;
      this.currentMainFilterItems = foundConfig.mainFilterItems;

      this.loading = true;
      this.loadMonths();
    }

    this._developerService.setPageDocumentMeta(this.currentConfig);
  }

  onFocus(): void {
    this.showAlgoliaResults = this.searchBoxState.query?.length > 0;
  }

  closeAlgoliaResults(): void {
    this.showAlgoliaResults = false;
  }

  ngAfterContentInit() {
    // ✅ By this point ALL child ngOnInit()s have already run,
    //    so all widgets are registered before the first API call fires.
    this.iss.start();
  }

  ngOnDestroy() {
    this._appNavService.setHideAppBar(false);
    this.isDestroyed = true;
    this.iss.removeWidgets([
      this.searchBoxWidget,
      this.hitsWidget,
      this.configureWidget
    ]);
  }

  clearSearchGrid() {
    this.searchParamModel = '';
    this.loadGridData();
  }

  onQueryChange(event: Event) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.searchBoxState?.refine((event.target as HTMLInputElement).value);
    }, 300);
  }

  openInsight(rowIdx, dataItem, navigateTo = 'overview') {
    this.navigateTo = navigateTo;
    this.selectedDeveloperName = dataItem.developerName;

    const foundDFilter = this._developerService.getFilterSelected(
      this.currentMainFilterItems,
      'key',
      'date'
    );
    this.selectedDate = foundDFilter?.value || '202512';

    this.showInsight = true;
    this.trackTableRowClicked(rowIdx, dataItem);
    this._router.navigate(['/know-your-developer'], {
      queryParams: { name: dataItem.developerName },
      queryParamsHandling: 'merge'
    });
  }

  openInsightWithFilter(event, name) {
    if ('buttons' in event && event.buttons == 1) {
      this.searchParamModel = name;
      this.showAlgoliaResults = false;
      this.searchBoxState?.refine(name);
      this.openInsight(0, { developerName: name });
      this.loadGridData();
    }
  }

  closeInsight() {
    this.showInsight = false;
    this.selectedDeveloperName = '';
    this._router.navigate(['/know-your-developer']);
  }

  selectMainFilter(
    selectedMainFilterItem: MainFilterItem,
    option: DropdownItem
  ) {
    if (selectedMainFilterItem.selected.value !== option.value) {
      selectedMainFilterItem.selected = option;

      const promises = [];
      // Reload dependency Facets based on dependency key and group
      this.currentMainFilterItems.forEach(mainFilterItem => {
        if (
          mainFilterItem.dependencies &&
          mainFilterItem.dependencies.length > 0
        ) {
          const foundDepdendency = mainFilterItem.dependencies.some(
            dependency =>
              (dependency.key === selectedMainFilterItem.key ||
                dependency.label === selectedMainFilterItem.label) &&
              (!dependency.group || dependency.group === this.selectedGroup)
          );
          if (foundDepdendency) {
            promises.push(
              this._developerService.loadOptions(
                mainFilterItem,
                this.currentConfig,
                this.selectedGroup
              )
            );
          }
        }
      });

      void Promise.all(promises).then(() => this.loadGridData());

      if (this.configureWidget) {
        this.iss.removeWidgets([this.configureWidget]);
        this.configureWidget = this.buildConfigureWidget();
        this.iss.addWidgets([this.configureWidget]);
      }
    }
  }

  generateFilterQuery() {
    const queryArr = [];
    const orQuaryArr = [];
    const mainFilterItems = this.currentConfig.mainFilterItems;

    mainFilterItems.forEach(mainFilterItem => {
      if (mainFilterItem.selected && mainFilterItem.algoliaKey) {
        const value =
          mainFilterItem?.selected?.value || mainFilterItem.options[0]?.value;
        const key = mainFilterItem.algoliaKey;
        if (value) {
          queryArr.push(`${key}:${value}`);
        } else if (key == 'kydOverallRisk') {
          OVERALL_RISK_OPTIONS.slice(1).forEach(option => {
            orQuaryArr.push(`${key}:${option.value}`);
          });
        }
      }
    });

    this.formattedFilterQuery = queryArr.join(' AND ');
    if (orQuaryArr.length) {
      this.formattedFilterQuery += ` AND (${orQuaryArr.join(' OR ')})`;
    }
  }

  private buildConfigureWidget(): Widget {
    this.generateFilterQuery();

    return configure({
      page: 0,
      hitsPerPage: 10,
      // formattedFilterQuery — your Algolia filter expression string
      // e.g. 'category:"shoes" AND price > 50 AND inStock:true'
      ...(this.formattedFilterQuery && { filters: this.formattedFilterQuery }),

      // Restrict full-text search to a subset of searchableAttributes.
      // Must be a subset of what's configured in your Algolia index settings.
      ...(this.restrictSearchableAttributes.length && {
        restrictSearchableAttributes: this.restrictSearchableAttributes
      }),

      // Limit which attributes are returned in each hit.
      // Use ['*'] to return all, or list only what your template needs
      // to reduce payload size.
      ...(this.attributesToRetrieve.length && {
        attributesToRetrieve: this.attributesToRetrieve
      })
    }) as unknown as Widget;
  }

  loadMonths() {
    let mainFilterItems = this.currentConfig.mainFilterItems;
    mainFilterItems = mainFilterItems.filter(
      filter => !filter.groups || filter.groups.indexOf(this.selectedGroup) >= 0
    );
    mainFilterItems.forEach(
      (mainFilter, index) => (mainFilter.routeParam = `param${index + 1}`)
    );
    const monthFilterItem = mainFilterItems.filter(
      filter => filter.key === 'date'
    )[0];
    if (monthFilterItem && monthFilterItem.options.length <= 0) {
      this.loading = true;
      void this._developerService
        .loadMonthOptions(
          this.currentConfig,
          mainFilterItems,
          monthFilterItem,
          this.selectedGroup
        )
        .then(() => {
          this.loading = false;
          this.loadGridData();
        });
    } else {
      this.loading = false;
      this.loadGridData();
    }
  }

  loadMore() {
    this.loadGridData(true);
  }

  loadGridData(isLoadMore = false) {
    if (!isLoadMore) {
      this.start = 0;
      this.gridData = [];
    }

    this.loadingData = true;
    this.showLoadMore = false;
    this.showLoadingMore = isLoadMore;

    const addParams = new Map();
    const routeParams = ['/know-your-developer'];
    const reportId = this.currentConfig.reportId;
    const mainFilterItems = this.currentConfig.mainFilterItems;

    // Set initial state of the addParams and routeParams
    mainFilterItems.forEach(mainFilterItem => {
      if (mainFilterItem.selected || mainFilterItem.label == 'Country') {
        let value =
          mainFilterItem?.selected?.value || mainFilterItem.options[0]?.value;
        if (value) {
          addParams.set(
            mainFilterItem.apiKey || mainFilterItem.key,
            encodeURIComponent(value)
          );
          routeParams.push(value);
        }
      }
    });

    addParams.set('group', this.selectedGroup);
    if (this.searchParamModel) {
      addParams.set('developerNameContains', this.searchParamModel);
    }

    // this._developerService.setPageDocumentMeta(this.currentConfig);

    // For Statistics
    void this._developerService
      .loadRiskStats(reportId, addParams)
      .then(facets => {
        if (facets && facets['overallRisk']) {
          const riskMap = {};
          let totalRisk = 0;

          facets['overallRisk'].forEach(risk => {
            for (const [key, value] of Object.entries(risk)) {
              riskMap[key] = value;
              totalRisk += Number(value);
            }
          });

          const riskFilter = mainFilterItems.filter(
            filter => filter.key === 'overallRisk'
          )[0];
          riskFilter.options.forEach(option => {
            option.count = riskMap[option.value];
          });
          riskFilter.options[0].count = totalRisk;
        }
      });

    // For Grid Data
    const sortColumns = this.currentConfig.gridHeaders
      .filter(header => header?.sort?.length > 0 && header.sortColumnKey)
      .map(
        header =>
          `${header.sortColumnKey} ${
            header.sort === 'sort-asc' ? 'asc' : 'desc'
          } ${
            header?.addSortColumnKeys
              ? ',' + header?.addSortColumnKeys.join(',')
              : ''
          }`
      );
    if (sortColumns.length > 0) {
      addParams.set('sortBy', sortColumns[0]);
    }

    const additionalQueryString =
      this._developerService.mapToUrlParam(addParams);
    const limit = 100;

    void this._developerService
      .getRankingsData(reportId, this.start, limit, additionalQueryString)
      .then(response => {
        if (response && response['docs']) {
          this.showLoadingMore = false;
          this.gridData = this.gridData.concat(response['docs']);

          const dataCount = this.gridData.length;
          this.start = dataCount;
          this.noResult = dataCount === 0;
          this.showLoadMore = response['numFound'] > dataCount;

          if (isLoadMore) {
            this._tracking.eventTrack('kyd_load_more', {
              items_loaded: dataCount
            });
          } else {
            this._tracking.eventTrack('kyd_page_load', {
              url: window.location.href
            });
          }
        }
        setTimeout(() => {
          this.loading = false;
          this.loadingData = false;

          if (!this.isDestroyed && this.selectedDeveloperName) {
            this.openInsight(0, { developerName: this.selectedDeveloperName });

            // void this._router.navigate(routeParams, {
            //   relativeTo: this._route,
            //   queryParams: this._route.snapshot.queryParams,
            //   queryParamsHandling: 'merge'
            // });
          }
        }, 300);
      });
  }

  trackTableRowClicked(idx, data) {
    this._tracking.eventTrack('kyd_row_clicked', {
      row_index: idx + 1,
      row_data: data
    });
  }
}
